import React, { useState } from "react";
import { makePagination } from "@components/common/TablePagination";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  theme,
} from "antd";
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ApiOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Sharp, tech-style input
const inputStyle = { borderRadius: 2 };

export default function CompanyAPI() {
  const { token } = theme.useToken();
  const [pageSize, setPageSize] = useState(10);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, "day"), dayjs()]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [form] = Form.useForm();

  // TODO: Fetch company options from API when backend is ready
  const companyOptions = [
    { value: "all", label: "All Companies" },
  ];

  // TODO: Fetch API keys from backend when API is ready
  const apiKeys = [];

  // Filter by company
  const filteredKeys = selectedCompany === "all"
    ? apiKeys
    : apiKeys.filter((k) => k.companyId === selectedCompany);

  // Stats
  const totalCalls = filteredKeys.reduce((sum, k) => sum + k.callsInRange, 0);
  const totalCallsToday = filteredKeys.reduce((sum, k) => sum + k.callsToday, 0);
  const activeKeys = filteredKeys.filter((k) => k.status === "active").length;

  // TODO: Fetch usage data from backend when API is ready
  const getUsageData = (keyId) => {
    return [];
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    message.success("API key copied to clipboard");
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const maskKey = (key) => {
    return key.substring(0, 12) + "..." + key.substring(key.length - 4);
  };

  const handleCreateKey = (values) => {
    message.success(`API key "${values.name}" created successfully`);
    setCreateModalOpen(false);
    form.resetFields();
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.id}</Text>
        </Space>
      ),
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "API Key",
      dataIndex: "key",
      key: "key",
      render: (key, record) => (
        <Space>
          <Text code style={{ fontSize: 12 }}>
            {visibleKeys[record.id] ? key : maskKey(key)}
          </Text>
          <Tooltip title={visibleKeys[record.id] ? "Hide" : "Show"}>
            <Button
              type="text"
              size="small"
              icon={visibleKeys[record.id] ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => toggleKeyVisibility(record.id)}
            />
          </Tooltip>
          <Tooltip title="Copy">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyKey(key)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "success" : "default"}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Calls (Selected Range)",
      dataIndex: "callsInRange",
      key: "callsInRange",
      align: "right",
      sorter: (a, b) => a.callsInRange - b.callsInRange,
      render: (calls) => <Text strong style={{ color: token.colorPrimary }}>{calls.toLocaleString()}</Text>,
    },
    {
      title: "Calls Today",
      dataIndex: "callsToday",
      key: "callsToday",
      align: "right",
      render: (calls) => calls.toLocaleString(),
    },
    {
      title: "Last Used",
      dataIndex: "lastUsed",
      key: "lastUsed",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => setSelectedApiKey(record)}
          >
            Usage
          </Button>
          <Button type="link" size="small" icon={<ReloadOutlined />}>
            Rotate
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            Revoke
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout active="api">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              API Management
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Manage company API keys and monitor usage
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setCreateModalOpen(true)}
            style={inputStyle}
          >
            Create API Key
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 24, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Company
            </Text>
            <Select
              value={selectedCompany}
              onChange={setSelectedCompany}
              options={companyOptions}
              style={{ width: 200, ...inputStyle }}
              size="large"
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Date Range
            </Text>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              size="large"
              style={{ width: 280, ...inputStyle }}
            />
          </div>
        </Space>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Active API Keys"
              value={activeKeys}
              prefix={<ApiOutlined />}
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Calls in Selected Range"
              value={totalCalls}
              valueStyle={{ color: token.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Calls Today"
              value={totalCallsToday}
              valueStyle={{ color: "#f5a623" }}
            />
          </Card>
        </Col>
      </Row>

      {/* API Keys Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={<Text strong>API Keys</Text>}
      >
        <Table
          columns={columns}
          dataSource={filteredKeys}
          rowKey="id"
          pagination={makePagination(pageSize, setPageSize)}
        />
      </Card>

      {/* Create API Key Modal */}
      <Modal
        title="Create API Key"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateKey}>
          <Form.Item
            name="company"
            label="Company"
            rules={[{ required: true, message: "Please select a company" }]}
          >
            <Select
              placeholder="Select company"
              options={companyOptions.filter((c) => c.value !== "all")}
              style={inputStyle}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Key Name"
            rules={[{ required: true, message: "Please enter a name" }]}
          >
            <Input placeholder="e.g., Production API" style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              rows={3}
              placeholder="Optional description for this API key"
              style={{ ...inputStyle, resize: "none" }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" style={inputStyle}>
                Create Key
              </Button>
              <Button onClick={() => setCreateModalOpen(false)} style={inputStyle}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Usage Detail Modal */}
      <Modal
        title={selectedApiKey ? `Usage: ${selectedApiKey.name}` : "API Usage"}
        open={!!selectedApiKey}
        onCancel={() => setSelectedApiKey(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedApiKey(null)} style={inputStyle}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {selectedApiKey && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Calls in Range"
                  value={selectedApiKey.callsInRange}
                  valueStyle={{ color: token.colorPrimary }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Calls Today"
                  value={selectedApiKey.callsToday}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="This Month"
                  value={selectedApiKey.callsThisMonth}
                />
              </Col>
            </Row>

            <Text strong style={{ display: "block", marginBottom: 16 }}>Daily API Calls</Text>
            <div style={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getUsageData(selectedApiKey.id)} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                  <defs>
                    <linearGradient id="callsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(v) => [v.toLocaleString(), "API Calls"]} />
                  <Area
                    type="monotone"
                    dataKey="calls"
                    name="API Calls"
                    stroke={token.colorPrimary}
                    fill="url(#callsGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Modal>
    </PageLayout>
  );
}
